import {sentryService} from '../../../services/SentryService';

import {DEPARTMENT_JOINS} from './departmentJoins';
import type {MenuTarget, MissedLink} from './menuTypes';

/** RP13: one event per browser session. */
const SESSION_KEY = 'dis1840.menuDrift.reported';

// Fallback when sessionStorage is missing (native) or throws (blocked storage).
let reportedInMemory = false;

/** Test seam: clears the module flag. */
export const __resetDriftReportForTests = () => {
  reportedInMemory = false;
};

const wasReported = (): boolean => {
  if (reportedInMemory) {
    return true;
  }
  try {
    return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
};

const markReported = () => {
  reportedInMemory = true;
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(SESSION_KEY, '1');
    }
  } catch {
    // The module flag above still holds for this page load.
  }
};

const describePaths = (paths: string[][]): string => paths.map(p => p.join(' / ')).join(' | ');

const describeTarget = (target: MenuTarget): string => {
  switch (target.kind) {
    case 'department':
      return target.path.join(' / ');
    case 'departments':
      return describePaths(DEPARTMENT_JOINS[target.join].paths);
    case 'specials':
      return ['Specials', ...(target.path ?? [])].join(' / ');
    case 'brand':
      return target.brand;
    case 'route':
      return String(target.route);
    case 'none':
      return '';
  }
};

/**
 * Reports links that missed at the selected store, at most once per browser session. The flag
 * is set only when Sentry actually sent the event (it is a no-op before init, C10).
 * RP14: the alert recipient is undecided; no alert routing is configured here.
 */
export const reportMenuDrift = ({
  missed,
  storeId,
  tenant,
}: {
  missed: MissedLink[];
  storeId: string;
  tenant: string;
}): void => {
  if (!missed.length || wasReported()) {
    return;
  }
  const sent = sentryService.captureMessage('MegaMenuDrift', {
    tags: {tenant, storeId},
    extra: {
      missed: missed.map(
        m =>
          `${m.menuId} > ${m.label} > ${describeTarget(m.target)}` +
          (m.missingPaths ? ` (missing: ${describePaths(m.missingPaths)})` : ''),
      ),
    },
  });
  if (sent) {
    markReported();
  }
};
